/* Copyright (c) 2022 Xperi. All Rights Reserved. */

import {
    Configuration,
    ConfigurationParameters,
    SessionCreate,
    SessionCreateRequestRequest,
    SessionDelete,
    SessionDeleteRequestRequest,
    SessionKeepAlive,
    SessionKeepAliveRequestRequest,
    SessionManagerApi,
} from 'client-code/session-manager';
import { Diagnostics } from '@tivocorp/tivo-webapp-loggers';
import { TServicesParams } from '../types/TServicesParams';
import { ISessionManagerServiceRepository } from '../interfaces/ISessionManagerServiceRepository';
import { Log } from '../logging/Log';
import { Session } from '../entities/Session';
import { TCreateSessionParams } from '../types/TCreateSessionParams';
import { TSessionStateParams } from '../types/TSessionStateParams';
import { Constants } from '../constants';
import { CommonHeaders } from '../CommonHeaders';
import { UniqueIdentifier } from '../utils/UniqueIdentifier';
import { TSession, TSessionState } from '../types';
import { APPLICATION_FEATURE_AREA } from '../../config';
import { SessionState } from '../entities/SessionState';
import { DeviceModel } from '../device';

export class SessionManagerServiceRepositoryImpl implements ISessionManagerServiceRepository {
    TAG: string = 'SessionManagerServiceRepositoryImpl';

    private mCommonHeaders = new CommonHeaders(APPLICATION_FEATURE_AREA.PLAYER).headers;

    private mParams: TServicesParams;

    private mSessionManagerApi: SessionManagerApi;

    constructor(params: TServicesParams) {
        this.mParams = params;

        const configurationParameters: ConfigurationParameters = {
            basePath: this.mParams.basePath,
            apiKey: this.mParams.accessToken,
            baseOptions: {
                timeout: Constants.SERVICE_CONNECTION_TIMEOUT,
                headers: this.mCommonHeaders,
            },
        };

        const configuration = new Configuration(configurationParameters);
        this.mSessionManagerApi = new SessionManagerApi(configuration);
    }

    private async createSessionFromSessionManager(
        sessionParams: TCreateSessionParams,
        sessionNetworkCallId?: number,
    ): Promise<TSession> {
        try {
            const sessionCreateRequest: SessionCreate = {
                deviceType: sessionParams.deviceType,
                drmType: sessionParams.drmType,
                partnerId: sessionParams.partnerId,
                partnerServicesCustomerId: sessionParams.partnerServicesCustomerId,
                partnerStationId: sessionParams.partnerStationId,
                transportEncodingType: sessionParams.transportEncodingType,
                tsn: this.mParams.bodyId,
                partnerRootOfferId: sessionParams.partnerRootOfferId,
                partnerAssetId: sessionParams.partnerAssetId,
                partnerServicesBodyId: DeviceModel.getCaDeviceId(),
            };

            const requestParameters: SessionCreateRequestRequest = {
                applicationFeatureArea: this.mCommonHeaders.ApplicationFeatureArea,
                applicationName: this.mCommonHeaders.ApplicationName,
                applicationVersion: this.mCommonHeaders.ApplicationVersion,
                bodyId: this.mParams.bodyId,
                deviceType: this.mCommonHeaders.DeviceType,
                originRequestId: UniqueIdentifier.generateOriginRequestId(),
                productName: this.mCommonHeaders.ProductName,
                sessionCreate: sessionCreateRequest,
            };

            const sessionManagerApiResponse = await this.mSessionManagerApi.sessionCreateRequest(
                requestParameters,
            );
            Diagnostics.info(
                this.TAG,
                'createSessionFromSessionManager',
                sessionManagerApiResponse.data,
            );
            Log.i(this.TAG, 'createSessionFromSessionManager', sessionManagerApiResponse.data);
            const session = new Session(sessionManagerApiResponse.data);
            return <TSession>{ session, sessionNetworkCallId };
        } catch (error: any) {
            error.sessionNetworkCallId = sessionNetworkCallId;
            Diagnostics.error(this.TAG, 'createSessionFromSessionManager', error);
            Log.e(this.TAG, 'createSessionFromSessionManager', error);
            throw error;
        }
    }

    private async sessionKeepAliveFromSessionManager(
        sessionStateParams: TSessionStateParams,
        sessionNetworkCallId?: number,
    ): Promise<TSessionState> {
        try {
            const sessionKeepAliveRequest: SessionKeepAlive = {
                sessionId: sessionStateParams.sessionId,
                partnerId: sessionStateParams.partnerId,
                partnerServicesCustomerId: sessionStateParams.partnerServicesCustomerId,
                tsn: this.mParams.bodyId,
            };

            const requestParameters: SessionKeepAliveRequestRequest = {
                applicationFeatureArea: this.mCommonHeaders.ApplicationFeatureArea,
                applicationName: this.mCommonHeaders.ApplicationName,
                applicationVersion: this.mCommonHeaders.ApplicationVersion,
                bodyId: this.mParams.bodyId,
                deviceType: this.mCommonHeaders.DeviceType,
                originRequestId: UniqueIdentifier.generateOriginRequestId(),
                productName: this.mCommonHeaders.ProductName,
                sessionKeepAlive: sessionKeepAliveRequest,
            };

            const sessionKeepAliveResponse = await this.mSessionManagerApi.sessionKeepAliveRequest(
                requestParameters,
            );
            Diagnostics.info(
                this.TAG,
                'sessionKeepAliveFromSessionManager: ',
                sessionKeepAliveResponse.data,
            );
            Log.i(this.TAG, 'sessionKeepAliveFromSessionManager: ', sessionKeepAliveResponse.data);
            const sessionState = new SessionState(sessionKeepAliveResponse.data);

            return <TSessionState>{ sessionState, sessionNetworkCallId };
        } catch (error: any) {
            error.sessionNetworkCallId = sessionNetworkCallId;
            Diagnostics.error(this.TAG, 'sessionKeepAliveFromSessionManager: ', error);
            Log.e(this.TAG, 'sessionKeepAliveFromSessionManager: ', error);
            throw error;
        }
    }

    private async sessionDeleteFromSessionManager(
        sessionStateParams: TSessionStateParams,
        sessionNetworkCallId?: number,
    ): Promise<TSessionState> {
        try {
            const sessionDeleteRequest: SessionDelete = {
                sessionId: sessionStateParams.sessionId,
                partnerId: sessionStateParams.partnerId,
                partnerServicesCustomerId: sessionStateParams.partnerServicesCustomerId,
                tsn: this.mParams.bodyId,
            };

            const requestParameters: SessionDeleteRequestRequest = {
                applicationFeatureArea: this.mCommonHeaders.ApplicationFeatureArea,
                applicationName: this.mCommonHeaders.ApplicationName,
                applicationVersion: this.mCommonHeaders.ApplicationVersion,
                bodyId: this.mParams.bodyId,
                deviceType: this.mCommonHeaders.DeviceType,
                originRequestId: UniqueIdentifier.generateOriginRequestId(),
                productName: this.mCommonHeaders.ProductName,
                sessionDelete: sessionDeleteRequest,
            };

            const sessionDeleteApiResponse = await this.mSessionManagerApi.sessionDeleteRequest(
                requestParameters,
            );
            Diagnostics.info(
                this.TAG,
                'sessionDeleteFromSessionManager: ',
                sessionDeleteApiResponse.data,
            );
            Log.i(this.TAG, 'sessionDeleteFromSessionManager: ', sessionDeleteApiResponse.data);
            const sessionState = new SessionState(sessionDeleteApiResponse.data);
            return <TSessionState>{ sessionState, sessionNetworkCallId };
        } catch (error: any) {
            error.sessionNetworkCallId = sessionNetworkCallId;
            Diagnostics.error(this.TAG, 'sessionDeleteFromSessionManager: ', error);
            Log.e(this.TAG, 'sessionDeleteFromSessionManager: ', error);
            throw error;
        }
    }

    /**
     * create playback session from Session Manager
     * @param sessionParams parameters to create session
     * @returns Session
     */
    public async createSession(
        sessionParams: TCreateSessionParams,
        sessionNetworkCallId?: number,
    ): Promise<TSession> {
        try {
            return await this.createSessionFromSessionManager(sessionParams, sessionNetworkCallId);
        } catch (error) {
            Diagnostics.error(this.TAG, 'createSession {error: ', error, '}');
            Log.e(this.TAG, 'createSession {error: ', error, '}');
            throw error;
        }
    }

    /**
     * keep alive playback session
     * @param session Session object
     * @returns SessionState object
     */
    public async sessionKeepAlive(
        session: Session,
        sessionNetworkCallId?: number,
    ): Promise<TSessionState> {
        try {
            return await this.sessionKeepAliveFromSessionManager(
                {
                    partnerId: session.partnerId,
                    partnerServicesCustomerId: session.partnerServicesCustomerId,
                    sessionId: session.sessionId,
                },
                sessionNetworkCallId,
            );
        } catch (error) {
            Diagnostics.error(this.TAG, 'sessionKeepAlive {error: ', error, '}');
            Log.e(this.TAG, 'sessionKeepAlive {error: ', error, '}');
            throw error;
        }
    }

    /**
     * delete playback session
     * @param session session object
     * @returns SessionState object
     */
    public async sessionDelete(
        session: Session,
        sessionNetworkCallId?: number,
    ): Promise<TSessionState> {
        try {
            return await this.sessionDeleteFromSessionManager(
                {
                    partnerId: session.partnerId,
                    partnerServicesCustomerId: session.partnerServicesCustomerId,
                    sessionId: session.sessionId,
                },
                sessionNetworkCallId,
            );
        } catch (error) {
            Diagnostics.error(this.TAG, 'sessionDelete {error:', error, '}');
            Log.e(this.TAG, 'sessionDelete {error:', error, '}');
            throw error;
        }
    }
}
